import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, FileArrowUp, Plus } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CustomerLoanHistoryTable } from "../components/CustomerLoanHistoryTable";
import { BlacklistDialog } from "../components/LoanDialogs";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const money = (amount) => {
  return new Intl.NumberFormat("en-MY", {
    style: "currency",
    currency: "MYR",
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const documentKinds = [
  { key: "ic_front", label: "IC Front" },
  { key: "ic_back", label: "IC Back" },
  { key: "agreement", label: "Agreement" },
];

export default function CustomerRecordPage({ user }) {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState("");
  const [deletingLoan, setDeletingLoan] = useState(null);
  const [editingDates, setEditingDates] = useState(null);
  const [blacklisting, setBlacklisting] = useState(null);
  const load = useCallback(async () => {
    try {
      const [recordResponse, documentResponse] = await Promise.all([
        api.get(`/customers/${customerId}/loan-history`),
        api.get(`/customers/${customerId}/documents`),
      ]);
      setRecord(recordResponse.data);
      setDocuments(documentResponse.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);
  const upload = async (kind, file) => {
    if (!file) {
      return;
    }
    const body = new FormData();
    body.append("kind", kind);
    body.append("file", file);
    try {
      await api.post(`/customers/${customerId}/documents`, body);
      toast.success("Document uploaded");
      load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    }
  };

  const deleteLoan = async () => {
    try {
      await api.delete(`/loans/${deletingLoan.id}`);
      toast.success("Loan removed");
      setDeletingLoan(null);
      load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    }
  };

  const saveLoanDates = async (event) => {
    event.preventDefault();
    try {
      await api.put(`/loans/${editingDates.id}/dates`, {
        loan_date: editingDates.loan_date,
        next_due_date: editingDates.next_due_date,
      });
      toast.success("Loan dates updated");
      setEditingDates(null);
      load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    }
  };

  if (error) {
    return (
      <div className="page customer-record-page" data-testid="customer-record-error">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="customer-record-error-back">
          <ArrowLeft size={16} /> Back
        </Button>
        <div className="empty-state" data-testid="customer-record-error-message">{error}</div>
      </div>
    );
  }

  if (!record) {
    return <div className="page-loading" data-testid="customer-record-loading">Loading customer record…</div>;
  }

  const customer = record.customer;
  const canDeleteLoan = ["admin", "master"].includes(user?.role);
  const canEditDates = ["admin", "master"].includes(user?.role);
  return (
    <div className="page customer-record-page" data-testid="customer-record-page">
      <button
        className="ledger-back-button"
        onClick={() => navigate(-1)}
        data-testid="customer-record-back-button"
      >
        <ArrowLeft size={16} /> Back to Customers
      </button>
      <section className="customer-record-header">
        <div className="customer-record-identity">
          <div className="customer-record-avatar">{customer.full_name.slice(0, 2).toUpperCase()}</div>
          <div>
            <h1>{customer.full_name}</h1>
            <p>{customer.nickname || "No nickname"} · {customer.ic_passport}</p>
          </div>
        </div>
        <Button onClick={() => navigate("/new-loans")} data-testid="customer-record-new-loan-button">
          <Plus size={16} /> New Loan
        </Button>
        <div className="customer-record-info">
          <div><span>Nickname</span><strong>{customer.nickname || "—"}</strong></div>
          <div><span>IC Number</span><strong>{customer.ic_passport}</strong></div>
          <div><span>Agent Name</span><strong>{customer.owner_name}</strong></div>
          <div><span>Address</span><strong>{customer.address}</strong></div>
          <div><span>Notes</span><strong>{customer.occupation}</strong></div>
        </div>
      </section>
      <section className="customer-outstanding-band">
        <span>Total Outstanding Amount</span>
        <strong>{money(record.total_outstanding)}</strong>
      </section>
      <section className="customer-history-section">
        <h2>Loan History</h2>
        <CustomerLoanHistoryTable
          canDelete={canDeleteLoan}
          canEditDates={canEditDates}
          customer={customer}
          loans={record.loans}
          onBlacklist={setBlacklisting}
          onDelete={setDeletingLoan}
          onEditDates={setEditingDates}
          onView={(loan) => navigate(`/loan-ledger/${loan.id}`)}
        />
      </section>
      <section className="customer-documents-section">
        <h2>Documents</h2>
        <div className="customer-document-grid">
          {documentKinds.map((kind) => {
            const document = documents.find((item) => item.kind === kind.key);
            return (
              <label
                className="customer-document-card"
                key={kind.key}
                data-testid={`document-${kind.key}`}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  data-testid={`document-upload-${kind.key}`}
                  onChange={(event) => upload(kind.key, event.target.files?.[0])}
                />
                <FileArrowUp size={22} />
                <strong>{kind.label}</strong>
                <span>{document ? document.original_filename : "Click to upload"}</span>
                {document && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      window.open(
                        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${document.id}/download`,
                        "_blank",
                      );
                    }}
                    data-testid={`view-document-${kind.key}`}
                  >
                    View
                  </button>
                )}
              </label>
            );
          })}
        </div>
      </section>
      <Dialog open={Boolean(deletingLoan)} onOpenChange={(open) => !open && setDeletingLoan(null)}>
        <DialogContent data-testid="delete-customer-loan-dialog">
          <DialogHeader>
            <DialogTitle>Remove this loan?</DialogTitle>
            <DialogDescription>
              This permanently removes the selected loan, its repayment records, and its schedule.
            </DialogDescription>
          </DialogHeader>
          <div className="dialog-actions">
            <Button
              variant="outline"
              onClick={() => setDeletingLoan(null)}
              data-testid="cancel-delete-customer-loan-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteLoan}
              data-testid="confirm-delete-customer-loan-button"
            >
              Remove Loan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(editingDates)} onOpenChange={(open) => !open && setEditingDates(null)}>
        <DialogContent data-testid="edit-loan-dates-dialog">
          <DialogHeader>
            <DialogTitle>Edit Loan Dates</DialogTitle>
            <DialogDescription>
              Update the loan start date and the next due date for this loan.
            </DialogDescription>
          </DialogHeader>
          <form className="dialog-form" onSubmit={saveLoanDates}>
            <Label htmlFor="edit-loan-date">Loan Date</Label>
            <Input
              id="edit-loan-date"
              data-testid="edit-loan-date-input"
              type="date"
              value={editingDates?.loan_date || ""}
              onChange={(event) => {
                setEditingDates((current) => ({ ...current, loan_date: event.target.value }));
              }}
              required
            />
            <Label htmlFor="edit-next-due-date">Next Due Date</Label>
            <Input
              id="edit-next-due-date"
              data-testid="edit-next-due-date-input"
              type="date"
              value={editingDates?.next_due_date || ""}
              onChange={(event) => {
                setEditingDates((current) => ({
                  ...current,
                  next_due_date: event.target.value,
                }));
              }}
              required
            />
            <Button type="submit" data-testid="save-loan-dates-button">Save Dates</Button>
          </form>
        </DialogContent>
      </Dialog>
      <BlacklistDialog
        loan={blacklisting}
        onClose={() => setBlacklisting(null)}
        onDone={load}
      />
    </div>
  );
}